import { PageController } from '@client/components/_core/layout/PageController';
import { Loading } from '@client/components/_ui/feedback/Loading';
import { isTrainingManagerOnlyUser } from '@client/hooks/auth/useIsTrainingManagerOnly';
import { useOrg } from '@client/hooks/business/org/useOrg';
import { useNavigate } from '@client/hooks/navigation/useNavigate';
import { selectIsLiteOrg, selectOrg, selectUser, useAuthStore } from '@client/stores/AuthStore';
import { queryClient, trpcOptions } from '@client/trpc/client';
import { MY_ITEMS_EXTRAS_PREFETCH_OPTIONS, USER_OWNED_ITEMS_COUNTS_PREFETCH_OPTIONS } from '@client/trpc/query-options';
import type { ShareableRouteContext } from '@client/types/router';
import { TokenRefreshManager } from '@client/utils/auth/TokenRefreshManager';
import { getFlagSync } from '@client/utils/feature-flags';
import * as Sentry from '@sentry/react';
import { isToolsOnlyOrg } from '@shared/constants/system/app';
import { createFileRoute, redirect } from '@tanstack/react-router';
import { zodValidator } from '@tanstack/zod-adapter';
import { Home as HomeIcon } from 'lucide-react';
import { useEffect, useRef } from 'react';
import { Home } from './Home';
import {
    HOME_DASHBOARD_IMAGE_PATH,
    HOME_DASHBOARD_IMAGE_SIZES,
    HOME_DASHBOARD_IMAGE_SRCSET,
} from './home-dashboard-image';
import {
    MY_ITEMS_V2_FLAG_KEY,
    shouldPrefetchHomeDashboardCounts,
    shouldRetryMyItemsExtras,
} from './home-dashboard-prefetch';
import { getHomeRoleRedirectTarget } from './home-role-redirect';
import { HOME_SEARCH_SCHEMA } from './home-search';

const HOME_DASHBOARD_IMAGE_PRELOAD_ID = 'home-dashboard-image-preload';

function preloadHomeDashboardImage(): void {
    if (typeof document === 'undefined' || document.getElementById(HOME_DASHBOARD_IMAGE_PRELOAD_ID)) {
        return;
    }

    const link = document.createElement('link');
    link.id = HOME_DASHBOARD_IMAGE_PRELOAD_ID;
    link.rel = 'preload';
    link.as = 'image';
    link.href = HOME_DASHBOARD_IMAGE_PATH;
    link.setAttribute('imagesrcset', HOME_DASHBOARD_IMAGE_SRCSET);
    link.setAttribute('imagesizes', HOME_DASHBOARD_IMAGE_SIZES);
    link.setAttribute('fetchpriority', 'high');
    document.head.appendChild(link);
}

export const Route = createFileRoute('/console/$orgId/')({
    staticData: {
        // The first five roles render Home (`accounts` since CUR-2675). The rest are listed
        // only so their cold load reaches the role redirect instead of RouteAccessGuard's
        // NotFound: the redirect sends every user without an original Home role to an
        // accessible landing, so they never render Home. `user_manager` needs no entry: it
        // is always paired with `user` (CUR-846).
        roles: [
            'user',
            'shared_edit_user',
            'org_admin',
            'training_manager',
            'accounts',
            'fleet_portal_user',
            'fmc_coordinator',
            'attestations_user',
        ],
        restrictToEmployee: false,
        ui: {
            hideTitle: true,
            widthVariant: 'default',
            icon: HomeIcon,
        },
        breadcrumb: {
            title: 'Home',
        },
    },
    validateSearch: zodValidator(HOME_SEARCH_SCHEMA),
    beforeLoad: ({ context, params, cause }): ShareableRouteContext => {
        // Tools-only orgs land on a dedicated simple home page. Redirect at the
        // router layer when the auth store is already hydrated (typical case for
        // in-app SPA navigation) — avoids one render of the dashboard before the
        // component fallback runs. When the store isn't populated yet (rare
        // cold-load path before `RequireAuth` hydrates), `HomePage` performs a
        // guarded one-shot navigation after hydration.
        // Only redirect when the hydrated org actually matches the URL's orgId.
        // During an org switch, `RequireAuth` hasn't yet swapped the store to
        // `params.orgId`, so the previous org's modules can't be trusted to
        // decide the target org's landing page — fall through to the
        // component-level fallback once the store catches up.
        const authState = useAuthStore.getState();
        const org = selectOrg(authState);
        const user = selectUser(authState);

        // Users without a Home role need a workspace they can access. This also
        // keeps them out of RouteAccessGuard's cold-load fallback.
        const roleRedirectTarget = user ? getHomeRoleRedirectTarget(user.roles) : null;
        if (org?.id === params.orgId && roleRedirectTarget !== null) {
            throw redirect({
                to: roleRedirectTarget,
                params,
                replace: true,
            });
        }

        if (org?.id === params.orgId && isToolsOnlyOrg(org.enabledModules)) {
            throw redirect({
                to: '/console/$orgId/tools-only-home-page',
                params,
                replace: true,
            });
        }

        // The course image (LCP element) only renders for orgs with the
        // `compliance` module that are not Lite (see `Home.tsx` → `ComplianceCourseCard`;
        // a Lite org's Home has no right column, CUR-2683). When the hydrated org
        // matches the URL and lacks that module or is Lite, skip the preload to
        // avoid downloading an image that never paints (and the resulting Chrome
        // "preloaded but not used" warning). On the cold-load / org-switch path
        // (`org` null or mismatched) the module list and tier can't be trusted yet, so
        // preloading there remains a reasonable best-effort tradeoff.
        const orgMatchesUrl = org?.id === params.orgId;
        if (!orgMatchesUrl || (org.enabledModules.includes('compliance') && !selectIsLiteOrg(authState))) {
            preloadHomeDashboardImage();
        }

        // Kick off the dashboard counts query while the route's chunks are still
        // being parsed and React is initialising. By the time `Home` mounts the
        // tRPC cache is warm and the metric grid (LCP element) can paint without
        // waiting on a render-time fetch. Mirrors the pattern used by
        // `~controls/~register/~details.$id/~route.tsx`, which lands at p75 LCP
        // ≈ 140ms vs the dashboard's pre-fix ≈ 5 678ms. With My items v2 on, the
        // extras are warmed in the same batch because the grid's skeleton waits on both.
        //
        // A same-route search update already has the live query observer and cache,
        // so do not refire this cold-navigation optimisation for Cards/Focus toggles.
        // Fire-and-forget: failures fall through to render-time `useQuery` retry. A
        // prefetch still in flight when Home mounts keeps its own retry policy, so the
        // extras prefetch carries Home's (`shouldRetryMyItemsExtras`).
        if (
            shouldPrefetchHomeDashboardCounts({
                hasValidToken: authState.hasValidToken({
                    minValidityMs: TokenRefreshManager.REFRESH_BUFFER_MS,
                }),
                isTrainingManagerOnly: user ? isTrainingManagerOnlyUser(user.roles) : false,
                hydratedOrg: org,
                routeOrgId: params.orgId,
                navigationCause: cause,
            })
        ) {
            void context.trpcUtils.dashboard.getUserOwnedItemsCounts.prefetch(undefined, {
                ...USER_OWNED_ITEMS_COUNTS_PREFETCH_OPTIONS,
            });
            if (getFlagSync(MY_ITEMS_V2_FLAG_KEY, false)) {
                void queryClient.prefetchQuery(
                    trpcOptions.dashboard.getMyItemsExtras.queryOptions(undefined, {
                        ...MY_ITEMS_EXTRAS_PREFETCH_OPTIONS,
                        retry: shouldRetryMyItemsExtras,
                    }),
                );
            }
        }
        return undefined;
    },
    component: HomePage,
});

function HomePage() {
    const org = useOrg();
    const user = useAuthStore(selectUser);
    const navigate = useNavigate();
    const roleRedirectTarget = user ? getHomeRoleRedirectTarget(user.roles) : null;
    const redirectTarget = roleRedirectTarget
        ? roleRedirectTarget
        : isToolsOnlyOrg(org.enabledModules)
          ? '/console/$orgId/tools-only-home-page'
          : null;
    const startedRedirectTarget = useRef<string | null>(null);

    useEffect(() => {
        if (redirectTarget === null) {
            startedRedirectTarget.current = null;
            return;
        }
        if (startedRedirectTarget.current !== redirectTarget) {
            startedRedirectTarget.current = redirectTarget;
            navigate({ to: redirectTarget, params: { orgId: org.id }, replace: true });
        }
    }, [navigate, org.id, redirectTarget]);

    Sentry.getCurrentScope().setTransactionName('Home');

    // Keep redirected users on a loading state until the one-shot navigation completes.
    if (redirectTarget !== null) {
        return <Loading />;
    }

    return (
        <PageController route={Route}>
            <Home />
        </PageController>
    );
}
